import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { or } from 'tio-ui/utilities';

// ---------------------------------------------------------------------------
// Checkbox – reimagined single checkbox with Tailwind styling
// ---------------------------------------------------------------------------

export type CheckboxSize = 'sm' | 'md' | 'lg';

interface CheckboxSignature {
  Args: {
    /** Whether the checkbox is checked */
    checked?: boolean;
    /** Called when the checked state changes */
    onChange?: (checked: boolean) => void;
    /** Label text displayed next to the checkbox */
    label?: string;
    /** Hint text displayed below the label */
    hint?: string;
    /** Validation error messages */
    errors?: string[];
    /** Disable the checkbox */
    isDisabled?: boolean;
    /** Show indeterminate state (overrides checked visually) */
    isIndeterminate?: boolean;
    /** Size variant @default "md" */
    size?: CheckboxSize;
  };
  Element: HTMLInputElement;
}

// ---------------------------------------------------------------------------
// Size classes
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Record<CheckboxSize, { box: string; label: string; icon: string }> = {
  sm: { box: 'w-3.5 h-3.5', label: 'text-sm', icon: 'w-2.5 h-2.5' },
  md: { box: 'w-4 h-4', label: 'text-sm', icon: 'w-3 h-3' },
  lg: { box: 'w-5 h-5', label: 'text-base', icon: 'w-3.5 h-3.5' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined Checkbox — a zero-dependency checkbox component.
 *
 * Controlled checkbox that fires `@onChange` with the new boolean value.
 * Supports indeterminate state, labels, hints, and error display.
 *
 * @example Basic usage
 * ```gts
 * import Checkbox from 'tio-reimagined/components/checkbox';
 *
 * <Checkbox
 *   @label="I agree to the terms"
 *   @checked={{this.agreed}}
 *   @onChange={{this.setAgreed}}
 * />
 * ```
 *
 * @example With errors
 * ```gts
 * <Checkbox
 *   @label="Accept privacy policy"
 *   @checked={{this.accepted}}
 *   @onChange={{this.setAccepted}}
 *   @errors={{array "You must accept the privacy policy"}}
 * />
 * ```
 */
export default class Checkbox extends Component<CheckboxSignature> {
  private _fieldId = `reimagined-checkbox-${guidFor(this)}`;

  private get size(): CheckboxSize {
    return this.args.size ?? 'md';
  }

  private get sizeClasses() {
    return SIZE_CLASSES[this.size];
  }

  private get hasErrors(): boolean {
    return Boolean(this.args.errors?.length);
  }

  handleChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.args.onChange?.(input.checked);
  };

  <template>
    <div
      data-test-checkbox
      class="flex flex-col gap-1 {{if @isDisabled 'opacity-50'}}"
      ...attributes
    >
      <label
        data-test-checkbox-label
        for={{this._fieldId}}
        class="flex items-start gap-2.5 cursor-pointer select-none
          {{if @isDisabled 'cursor-not-allowed'}}"
      >
        {{! Custom checkbox visual }}
        <span class="relative flex-shrink-0 mt-0.5">
          <input
            id={{this._fieldId}}
            type="checkbox"
            checked={{@checked}}
            disabled={{@isDisabled}}
            aria-checked={{if @isIndeterminate "mixed" (if @checked "true" "false")}}
            aria-invalid={{if this.hasErrors "true"}}
            class="peer sr-only"
            {{on "change" this.handleChange}}
          />
          <span
            class="block rounded border transition-colors duration-150
              {{this.sizeClasses.box}}
              {{if
                (or @isIndeterminate @checked)
                'bg-violet-500 border-violet-500 dark:bg-violet-500 dark:border-violet-500'
                (if
                  this.hasErrors
                  'border-red-500 dark:border-red-400'
                  'border-gray-300 dark:border-gray-600 peer-hover:border-gray-400 dark:peer-hover:border-gray-500'
                )
              }}
              peer-focus-visible:ring-2 peer-focus-visible:ring-violet-500/20 peer-focus-visible:ring-offset-1"
          >
            {{#if @isIndeterminate}}
              {{! Indeterminate mark (takes priority over checked) }}
              <svg
                class="{{this.sizeClasses.icon}} text-white"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 6H9.5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            {{else if @checked}}
              {{! Check mark SVG }}
              <svg
                class="{{this.sizeClasses.icon}} text-white"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 6L5 8.5L9.5 3.5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            {{/if}}
          </span>
        </span>

        {{! Label + hint text }}
        <div class="flex flex-col">
          {{#if @label}}
            <span class="{{this.sizeClasses.label}} font-medium text-gray-700 dark:text-gray-300">
              {{@label}}
            </span>
          {{/if}}
          {{#if @hint}}
            <span class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{@hint}}</span>
          {{/if}}
        </div>
      </label>

      {{! Error messages }}
      {{#if this.hasErrors}}
        <div data-test-checkbox-errors class="ml-6 space-y-0.5">
          {{#each @errors as |error|}}
            <p class="text-xs text-red-600 dark:text-red-400" role="alert">{{error}}</p>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

export type { CheckboxSignature };
